import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Phone, ShieldCheck, ArrowLeft, MessageCircle, Send, ChevronDown, Search } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LangContext';
import { useTheme } from '@/contexts/ThemeContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import COUNTRY_CODES, { DEFAULT_COUNTRY } from '@/data/countryCodes';
import { signInWithGoogle, signInWithApple } from '@/lib/firebaseAuth';
import { Capacitor } from '@capacitor/core';

export default function Login() {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [stage, setStage] = useState('phone');
  const [hint, setHint] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(true);  // defaulted CHECKED per product spec
  const [country, setCountry] = useState(DEFAULT_COUNTRY); // iter-80
  const [pickerOpen, setPickerOpen] = useState(false);
  const { login } = useAuth();
  const { t, lang } = useLang();
  const { resolvedTheme } = useTheme();
  const brandSrc = resolvedTheme === 'dark' ? '/becaim-wordmark-dark.png' : '/becaim-wordmark.png';
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const refCode = search.get('ref') || undefined;

  const isIndia = country.code === '+91';
  const minDigits = country.minDigits || 8;
  const maxDigits = country.maxDigits || 12;

  const validatePhone = () => {
    if (!agreed) {
      toast.error('Please accept the Terms & Privacy Policy to continue');
      return false;
    }
    if (!/^\d+$/.test(phone) || phone.length < minDigits || phone.length > maxDigits) {
      toast.error(`Enter a valid ${minDigits === maxDigits ? minDigits : `${minDigits}-${maxDigits}`}-digit phone for ${country.country}`);
      return false;
    }
    return true;
  };

  const sendOtp = async (channel) => {
    if (!validatePhone()) return;
    if (channel === 'sms' && !isIndia) {
      toast.error('SMS is available for India (+91) only. Please use WhatsApp.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/send-otp', {
        phone,
        channel,
        country_code: country.code,
      });
      setStage('otp');
      const ch = data.channel;
      if (data.dev_otp) setHint(`Dev OTP: ${data.dev_otp} (Pingbix delivery failed)`);
      else if (ch === 'admin_bypass') setHint('Use your admin OTP');
      else if (ch === 'whatsapp') setHint('OTP sent via WhatsApp');
      else if (ch === 'sms') setHint('OTP sent via SMS');
      toast.success('OTP sent');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    if (!/^\d{4,8}$/.test(otp)) {
      toast.error('Enter a valid OTP');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/verify-otp', {
        phone,
        otp,
        country_code: country.code,
        language: lang,
        ref: refCode,
      });
      login(data.token, data.user);
      if (data.user.role === 'super_admin') navigate('/admin');
      else if (!data.user.username || !data.user.display_name) navigate('/setup-username');
      else if (!data.user.onboarded) navigate('/onboarding');
      else navigate('/app');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  // iter-93: Firebase Google + Apple Sign-In replaces Emergent Google.
  // Both flows: get an OIDC id_token, POST to backend, backend mints app JWT.
  const finishOauthLogin = (data) => {
    login(data.token, data.user);
    if (data.user.role === 'super_admin') navigate('/admin');
    else if (!data.user.username || !data.user.display_name) navigate('/setup-username');
    else if (!data.user.onboarded) navigate('/onboarding');
    else navigate('/app');
  };

  const handleGoogleLogin = async () => {
    if (!agreed) {
      toast.error('Please accept the Terms & Privacy Policy to continue');
      return;
    }
    setLoading(true);
    try {
      const { idToken } = await signInWithGoogle();
      const { data } = await api.post('/auth/firebase/google', {
        id_token: idToken,
        language: lang,
        ref: refCode,
      });
      finishOauthLogin(data);
    } catch (e) {
      // signInWithPopup throws on popup-closed-by-user, popup-blocked, etc.
      const code = e?.code || '';
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        // Silent — user just dismissed the popup.
      } else {
        // iter-97: surface full error via native alert() so iOS users can
        // actually see the runtime error message. JS console.log() does NOT
        // appear in Xcode's device console — only Safari Web Inspector — so
        // we route the message through window.alert which bridges through
        // Capacitor to a native UIAlertController on iOS.
        const isNative = (
          typeof Capacitor !== 'undefined'
          && Capacitor.isNativePlatform
          && Capacitor.isNativePlatform()
        );
        const detail = e?.response?.data?.detail;
        const msg = e?.message || '';
        const errCode = e?.code || '';
        const fallback = detail || msg || 'Google sign-in failed';
        if (isNative) {
          // Native alert surfaces enough detail to diagnose Info.plist /
          // REVERSED_CLIENT_ID issues, missing GoogleService-Info.plist,
          // Firebase provider not enabled, etc.
          try {
            window.alert(
              'Google sign-in failed\n\n'
              + `code: ${errCode || '(none)'}\n`
              + `message: ${msg || '(none)'}\n`
              + `detail: ${detail || '(none)'}\n`
              + `raw: ${(() => { try { return JSON.stringify(e).slice(0, 600); } catch { return String(e); } })()}`
            );
          } catch { /* ignore */ }
        }
        toast.error(fallback);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    if (!agreed) {
      toast.error('Please accept the Terms & Privacy Policy to continue');
      return;
    }
    setLoading(true);
    try {
      const res = await signInWithApple();
      const { data } = await api.post('/auth/firebase/apple', {
        id_token: res.idToken,
        full_name: res.fullName || null,
        email: res.email || null,
        apple_sub: res.apple_sub || null,
        language: lang,
        ref: refCode,
      });
      finishOauthLogin(data);
    } catch (e) {
      const code = e?.code || '';
      const msg = e?.message || '';
      // Native plugin throws on user cancel — silent.
      if (msg.toLowerCase().includes('cancelled') || code === 'auth/popup-closed-by-user') {
        // ignore
      } else {
        toast.error(e?.response?.data?.detail || msg || 'Apple sign-in failed');
      }
    } finally {
      setLoading(false);
    }
  };

  // iter-94: Apple button visible ONLY inside the native iOS Capacitor app.
  // We don't ship the web Apple OAuth flow because it requires fully
  // configuring the Apple provider in Firebase Console (Services ID + Team
  // ID + Key ID + .p8) which the user is not setting up. Hiding on web +
  // Android prevents users from ever seeing a Firebase / Apple error.
  const showAppleButton = (
    typeof Capacitor !== 'undefined'
    && Capacitor.isNativePlatform
    && Capacitor.isNativePlatform()
    && Capacitor.getPlatform
    && Capacitor.getPlatform() === 'ios'
  );

  return (
    <div className="min-h-screen w-full bg-[#F7F9FC] flex items-center justify-center p-4 sm:p-6 gm-safe-top">
      <div className="w-full max-w-md relative flex flex-col md:bg-white md:rounded-3xl md:shadow-xl md:border md:border-[#E5E7EB] md:p-8">
        <div className="flex justify-between items-start pt-2">
        {stage === 'otp' ? (
          <button
            onClick={() => setStage('phone')}
            className="p-2 -ml-2 text-[#4B4B4B]"
            data-testid="login-back-button"
          >
            <ArrowLeft size={28} strokeWidth={3} />
          </button>
        ) : <div />}
        <LanguageSwitcher />
      </div>

      <div className="flex flex-col items-center text-center gap-4 mt-4">
        <button
          onClick={() => navigate('/')}
          aria-label="BecAim home"
          data-testid="login-logo-home"
          className="active:translate-y-0.5 transition-transform hover:brightness-105"
        >
          <img
            src={brandSrc}
            alt="BecAim AI"
            className="w-64 sm:w-72 h-auto select-none"
            draggable="false"
          />
        </button>
        <button
          onClick={() => navigate('/')}
          className="text-center group"
          data-testid="login-name-home"
        >
          <h1 className="sr-only">{t('app_name')}</h1>
          <p className="text-lg font-bold text-[#AFAFAF]">{t('tagline')}</p>
        </button>

        {stage === 'phone' ? (
          <div className="w-full space-y-4 gm-slide-up">
            <div className="text-left">
              <label className="text-sm font-extrabold text-[#AFAFAF] uppercase tracking-wide flex items-center gap-2">
                <Phone size={16} strokeWidth={3} /> {t('enter_phone')}
              </label>
              <div className="mt-2 flex items-stretch gap-2">
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="px-3 py-3 rounded-2xl border-2 border-[#E5E5E5] bg-white flex items-center gap-1.5 font-extrabold text-base shrink-0 hover:border-[#1CB0F6]"
                  data-testid="login-country-picker"
                >
                  <span className="text-xl">{country.flag}</span>
                  <span>{country.code}</span>
                  <ChevronDown size={14} strokeWidth={3} className="text-[#94A3B8]" />
                </button>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, maxDigits))}
                  inputMode="tel"
                  maxLength={maxDigits}
                  className="gm-input flex-1"
                  placeholder={`${minDigits === maxDigits ? minDigits : `${minDigits}-${maxDigits}`} digits`}
                  data-testid="login-phone-input"
                  onKeyDown={(e) => e.key === 'Enter' && sendOtp(isIndia ? 'sms' : 'whatsapp')}
                />
              </div>
            </div>

            {/* Agree checkbox — default CHECKED. Required for SMS/WhatsApp/Google. */}
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#1CB0F6]"
                data-testid="login-agree-checkbox"
              />
              <span className="text-[11px] font-bold text-[#475569] leading-snug">
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noreferrer" className="text-[#1CB0F6] underline" data-testid="login-terms-link">Terms &amp; Conditions</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#1CB0F6] underline" data-testid="login-privacy-link">Privacy Policy</a>.
              </span>
            </label>

            <div className={`grid ${isIndia ? 'grid-cols-2' : 'grid-cols-1'} gap-2`}>
              <button
                onClick={() => sendOtp('whatsapp')}
                disabled={loading}
                className="gm-btn w-full py-4 text-base"
                style={{ background: 'linear-gradient(135deg,#25D366,#128C7E)', color: 'white' }}
                data-testid="login-send-whatsapp"
              >
                <MessageCircle size={20} strokeWidth={2.6} /> WhatsApp
              </button>
              {isIndia && (
                <button
                  onClick={() => sendOtp('sms')}
                  disabled={loading}
                  className="gm-btn gm-btn-primary w-full py-4 text-base"
                  data-testid="login-send-sms"
                >
                  <Send size={18} strokeWidth={2.6} /> SMS
                </button>
              )}
            </div>
            <p className="text-[11px] text-[#94A3B8] font-bold text-center">
              {isIndia
                ? 'We\u2019ll send a 6-digit code. WhatsApp is fastest.'
                : 'WhatsApp OTP for your country. SMS is available for India only.'}
            </p>

            {/* Divider */}
            <div className="flex items-center gap-3 my-2">
              <div className="h-px flex-1 bg-[#E2E8F0]" />
              <span className="text-[11px] font-extrabold text-[#94A3B8] uppercase">or</span>
              <div className="h-px flex-1 bg-[#E2E8F0]" />
            </div>

            {/* iter-93 — Sign in with Google (Firebase) */}
            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="gm-btn w-full py-4 text-base bg-white border-2 border-[#E2E8F0] text-[#1F2937] font-extrabold flex items-center justify-center gap-2"
              data-testid="login-google-button"
            >
              <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden>
                <path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 3l5.7-5.7C33.7 6.2 29.1 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.7 1.1 7.8 3l5.7-5.7C33.7 6.2 29.1 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 12.9-5l-6-5.1c-1.9 1.3-4.3 2.1-6.9 2.1-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.7 16.1 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5h-1.9V20H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.8l6 5.1C40.9 35.6 44 30.2 44 24c0-1.3-.1-2.4-.4-3.5z"/>
              </svg>
              Continue with Google
            </button>

            {/* iter-93 — Sign in with Apple (Firebase web flow / native iOS plugin).
                Shown on Apple platforms only. */}
            {showAppleButton && (
              <button
                onClick={handleAppleLogin}
                disabled={loading}
                className="gm-btn w-full py-4 text-base bg-black border-2 border-black text-white font-extrabold flex items-center justify-center gap-2"
                data-testid="login-apple-button"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden fill="currentColor">
                  <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-3.062 1.57-.117 0-.23-.02-.3-.03-.018-.06-.05-.32-.05-.59 0-1.14.55-2.27 1.17-2.99.785-.93 2.106-1.63 3.16-1.67.046.18.06.42.06.62zm4.124 15.36c-.484 1.13-.71 1.64-1.336 2.64-.875 1.4-2.108 3.14-3.628 3.16-1.35.01-1.7-.88-3.53-.87-1.83.01-2.21.88-3.56.87-1.52-.01-2.69-1.59-3.56-2.99-2.45-3.93-2.71-8.55-1.2-11.01.99-1.62 2.66-2.57 4.22-2.57 1.59 0 2.59.87 3.91.87 1.28 0 2.06-.87 3.9-.87 1.39 0 2.87.76 3.91 2.07-3.44 1.88-2.88 6.8.87 8.7z"/>
                </svg>
                Continue with Apple
              </button>
            )}
          </div>
        ) : (
          <div className="w-full space-y-4 gm-slide-up">
            <div className="text-left">
              <label className="text-sm font-extrabold text-[#AFAFAF] uppercase tracking-wide flex items-center gap-2">
                <ShieldCheck size={16} strokeWidth={3} /> {t('otp_label')}
              </label>
              <p className="text-[11px] font-extrabold text-[#475569] mt-1">
                Sent to <span className="text-[#1CB0F6]">{country.flag} {country.code} {phone}</span>
              </p>
              <input
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                inputMode="numeric"
                maxLength={8}
                className="gm-input mt-2 text-center tracking-[0.5em] text-2xl"
                placeholder="••••••"
                data-testid="login-otp-input"
                onKeyDown={(e) => e.key === 'Enter' && verify()}
              />
              {hint && <p className="text-xs text-[#1CB0F6] mt-2 font-extrabold">{hint}</p>}
            </div>
            <button
              onClick={verify}
              disabled={loading}
              className="gm-btn gm-btn-success w-full text-xl py-4"
              data-testid="login-verify-button"
            >
              {loading ? '…' : t('verify')}
            </button>
          </div>
        )}
      </div>

      <p className="text-center text-xs text-[#AFAFAF] pb-2">
        © {new Date().getFullYear()} BecAim AI
      </p>
      </div>

      {pickerOpen && (
        <CountryPicker
          onClose={() => setPickerOpen(false)}
          onPick={(c) => { setCountry(c); setPhone(''); setPickerOpen(false); }}
        />
      )}
    </div>
  );
}

function CountryPicker({ onClose, onPick }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return COUNTRY_CODES;
    return COUNTRY_CODES.filter((c) =>
      c.country.toLowerCase().includes(term) ||
      c.code.includes(term) ||
      c.code.replace('+', '').includes(term)
    );
  }, [q]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
      data-testid="login-country-picker-modal"
    >
      <div
        className="bg-white dark:bg-[#1E293B] rounded-3xl w-full max-w-md max-h-[80dvh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 pt-4 pb-3 border-b border-[#E5E5E5]">
          <p className="text-base font-extrabold mb-2">Pick your country</p>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search country or dial code"
              className="w-full pl-9 pr-3 py-2 rounded-xl border-2 border-[#E5E5E5] text-sm font-bold"
              data-testid="login-country-search"
            />
          </div>
        </div>
        <ul className="flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]" data-testid="login-country-list">
          {filtered.map((c, idx) => (
            <li key={`${c.code}-${c.country}-${idx}`}>
              <button
                onClick={() => onPick(c)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#F1F5F9]"
                data-testid={`login-country-option-${c.country.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <span className="text-xl">{c.flag}</span>
                <span className="flex-1 text-sm font-extrabold">{c.country}</span>
                <span className="text-sm font-extrabold text-[#7E5BEF]">{c.code}</span>
              </button>
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="px-4 py-6 text-center text-sm font-bold text-[#94A3B8]">No countries match.</li>
          )}
        </ul>
      </div>
    </div>
  );
}
